import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ['source', 'destination']
  static values = {
    disableIfParentHidden: {
      type: Boolean, default: true
    }
  }

  connect() {
    this.toggle();

    // A page that refreshes by morphing (<meta name="turbo-refresh-method" content="morph">, e.g. after a
    // save) diffs the live DOM against the server HTML, which has none of the inline display or disabled
    // state set here, so it strips them. The elements survive, so connect() doesn't run again: re-apply.
    this.reapply = () => this.toggle();
    document.addEventListener('turbo:morph', this.reapply);
  }

  disconnect() {
    document.removeEventListener('turbo:morph', this.reapply);
  }

  toggle() {
    let enabled_element_class = this.relevantClass()

    this.destinationTargets.forEach(element => {
      if (element.className.indexOf(enabled_element_class) === -1) {
        this.toggleElement(element, 'off');
      } else {
        this.toggleElement(element, 'on');
      }
    })
  }

  relevantClass() {
    if (this.sourceTarget.type === 'checkbox') {
      if (this.sourceTarget.checked) {
        return this.sourceTarget.dataset.toggledElementClass
      } else {
        return null
      }
    } else {
      return this.relevantOption().dataset.toggledElementClass
    }
  }

  relevantOption() {
    let selected_option
    if (this.sourceTargets.length > 1) {
      selected_option = this.sourceTargets.filter((entry) => { return entry.checked })[0];
    } else {
      selected_option = this.sourceTarget.options[this.sourceTarget.selectedIndex];
    }

    return selected_option
  }

  toggleElement(element, state) {
    element.style.display = state === 'on' ? 'block' : 'none';
    // check visibility because even if this element is not hidden, the parent may be, in which case its actual
    // visibility is false, and we should disable it

   this.disableElement(element, state);
  }

  disableElement(element, state) {
    let disabled = this.shouldDisable(element, state);

    if (element.nodeName === 'FIELDSET') {
      element.disabled = disabled
      if (element.hasAttribute('required')) {
        element.required = !disabled
      }
    } else {
      let input = element.querySelectorAll('select, input, textarea');
      input.forEach((e) => {
        e.disabled = disabled
        if (e.hasAttribute('required')) {
          e.required = !disabled
        }
        // Dispatch event for other controllers to sync state
        e.dispatchEvent(new CustomEvent('toggle:disabled', {
          bubbles: false,
          detail: { disabled }
        }))
      })
    }
  }

  shouldDisable(element, state) {
    if (!this.disableIfParentHiddenValue) {
      return state === 'off'
    }

    if (state === 'off') {
      return true
    }

    // A toggled-on destination that is visible stays enabled.
    if (element.checkVisibility()) {
      return false
    }

    // It is toggled-on but not visible. Keep it disabled — EXCEPT when the ONLY thing hiding it is a
    // closed modal, which hides its own chrome with inline display:none and will reopen; such a field
    // must still submit (#3788). A field hidden by anything else — no modal at all (e.g. an inactive
    // toggle section on a normal page), or an inactive section inside the modal — stays disabled so
    // stale/partial data isn't submitted.
    const modal = element.closest('[data-modal-target="modal"]')
    if (!modal) {
      return true
    }

    return this.hiddenBySectionBefore(element, modal)
  }

  // True when a section between `element` and its closed `modal` is itself hidden — i.e. the field is
  // hidden by an inactive section, not merely by the closed modal. The modal's own chrome hides via
  // inline display:none but is neither a toggle destination nor a `.hidden`/[hidden] block, so it is
  // skipped; an inactive section IS one of those (radio-toggle uses `.hidden`, toggle uses an
  // inline-hidden destination).
  hiddenBySectionBefore(element, modal) {
    for (let node = element.parentElement; node && node !== modal; node = node.parentElement) {
      if (node.matches('.hidden, [hidden]')) {
        return true
      }

      if (
        node.matches('[data-toggle-target="destination"], [data-radio-toggle-target="destination"]') &&
        window.getComputedStyle(node).display === 'none'
      ) {
        return true
      }
    }

    return false
  }
}
